import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Sun, Moon, Rocket } from "lucide-react";

function PlayerCard({ p, i }) {
  return (
    <Link to={`/research/${p.mlb_id}`} data-testid={`likely-card-${p.mlb_id}`}
      className={`card-surface rounded-lg p-3.5 hover-lift block ${i < 3 ? "border-[#a855f7]/40 gold-glow" : ""}`}>
      <div className="flex items-center gap-3">
        <span className={`font-data text-lg font-black w-7 ${i < 3 ? "text-[#a855f7]" : "text-neutral-600"}`}>{i + 1}</span>
        <img src={p.headshot} alt="" className="w-10 h-10 rounded-full object-cover bg-white/5" loading="lazy" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate">{p.name}</div>
          <div className="text-[10px] text-neutral-500 truncate">{p.team} · vs {p.pitcher}{p.time_et ? ` · ${p.time_et}` : ""}</div>
        </div>
        <div className="text-right shrink-0">
          <div className={`font-data text-lg font-bold ${p.confidence >= 7 ? "text-[#22c55e]" : "text-[#f59e0b]"}`}>{p.confidence}</div>
          <div className={`text-[9px] uppercase tracking-widest font-bold ${p.style === "Chalk" ? "text-neutral-400" : p.style === "Long Shot" ? "text-fuchsia-400" : "text-cyan-400"}`}>{p.style}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mt-2.5">
        {p.tags.map((t) => (
          <span key={t} className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full border ${
            t === "Heater" ? "border-orange-400/50 text-orange-300 bg-orange-500/10" :
            t === "Weak Spot" ? "border-[#a855f7]/50 text-[#a855f7] bg-[#a855f7]/10" :
            t === "Form Rising" ? "border-[#22c55e]/50 text-[#22c55e] bg-[#22c55e]/10" :
            "border-white/15 text-neutral-400"}`}>{t}</span>
        ))}
        {p.tags.length === 0 && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full border border-white/15 text-neutral-500">Meter Play</span>}
      </div>
      <div className="grid grid-cols-3 gap-1.5 mt-2.5 text-center">
        {[["HR Prob", `${p.hr_probability}%`], ["HR L15 G", p.hr_last15], ["Best Line", p.best_odds > 0 ? `+${p.best_odds}` : p.best_odds]].map(([l, v]) => (
          <div key={l} className="bg-black/40 border border-white/10 rounded p-1.5">
            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">{l}</div>
            <div className="font-data text-xs sm:text-sm font-bold">{v}</div>
          </div>
        ))}
      </div>
    </Link>
  );
}

export default function LikelyHR() {
  const { data, isLoading } = useQuery({ queryKey: ["likelyHR"], queryFn: () => api.get("/likely-hr").then((r) => r.data) });
  const { data: trackerData } = useQuery({ queryKey: ["confidenceTracker"], queryFn: () => api.get("/confidence/tracker").then((r) => r.data) });
  const players = data?.players || [];
  const day = players.filter((p) => p.slot === "day");
  const night = players.filter((p) => p.slot !== "day");
  const summary = trackerData?.summary;

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Likely To <span className="gold-text">HR</span></h1>
        <p className="text-neutral-500 text-sm mt-1">The {players.length || 15} bats most likely to go yard today — scored on ballpark, pitcher, weak spots, heaters, rising form and sweet-spot matchups. Chalk, value and long shots all make the list.</p>
      </div>

      {summary && (summary.wins + summary.losses) > 0 && (
        <div className="card-gold border border-[#a855f7]/30 rounded-xl p-4 flex flex-wrap items-center gap-x-8 gap-y-2 fade-up-1" data-testid="likely-record-strip">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-bold">Predictor Record{summary.date ? ` — ${summary.date}` : ""}</div>
            <div className="font-data text-2xl font-black">{summary.wins}<span className="text-neutral-600">-</span>{summary.losses}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-bold">Hit Rate</div>
            <div className={`font-data text-2xl font-black ${summary.win_pct >= 50 ? "text-[#22c55e]" : "text-[#f59e0b]"}`}>{summary.win_pct}%</div>
          </div>
          <p className="text-xs text-neutral-500 flex-1 min-w-[200px]">Graded fresh every day against the box scores — the record resets daily, so what you see is yesterday's truth, not a cherry-picked streak.</p>
        </div>
      )}

      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Crunching today's slate...</div>}
      {!isLoading && players.length === 0 && (
        <div className="card-surface rounded-xl p-10 text-center" data-testid="likely-empty">
          <Rocket className="mx-auto text-neutral-700 mb-3" size={26} />
          <p className="text-neutral-400">No slate today — the list loads on game days.</p>
        </div>
      )}

      {day.length > 0 && (
        <div className="fade-up-1" data-testid="likely-day-section">
          <div className="flex items-center gap-2 mb-1">
            <Sun size={16} className="text-[#f59e0b]" />
            <h2 className="font-display text-xl font-bold uppercase">Day <span className="text-[#a855f7]">Slate</span></h2>
            <span className="text-xs text-neutral-500">{day.length} bats</span>
          </div>
          <div className="accent-bar w-32 mb-3" />
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {day.map((p) => <PlayerCard key={p.mlb_id} p={p} i={players.indexOf(p)} />)}
          </div>
        </div>
      )}
      {night.length > 0 && (
        <div className="fade-up-2" data-testid="likely-night-section">
          <div className="flex items-center gap-2 mb-1">
            <Moon size={16} className="text-[#38bdf8]" />
            <h2 className="font-display text-xl font-bold uppercase">Night <span className="text-[#a855f7]">Slate</span></h2>
            <span className="text-xs text-neutral-500">{night.length} bats</span>
          </div>
          <div className="accent-bar w-32 mb-3" />
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {night.map((p) => <PlayerCard key={p.mlb_id} p={p} i={players.indexOf(p)} />)}
          </div>
        </div>
      )}
    </div>
  );
}
